import React from 'react';
import { Box, Typography, Skeleton, Link as MuiLink } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../../i18n/useAppTranslation';
import { commandDashboard } from '../commandDashboardTokens';
import type { ReorderCardModel, ReorderCardText, ReorderCardTone } from '../../storeHome/deriveReorderCard';

interface ReorderCardProps {
  model: ReorderCardModel;
  loading?: boolean;
  /** The flag is on but the plan lacks Advanced analytics: show the upgrade line instead of rows. */
  planLocked?: boolean;
  error?: string | null;
}

export const REORDER_TABLE_HREF = '/app/sales-report#reorder';

const dotColor = (tone: ReorderCardTone): string =>
  tone === 'red' ? commandDashboard.color.dotRed : tone === 'amber' ? commandDashboard.color.dotAmber : commandDashboard.color.inkSubtle;

/**
 * Store home "Reorder" card (flag `predictive_reorder`, plan `advancedAnalytics`;
 * MOT-149): the products to order now, then to watch, then to count, with one
 * line each, and a link to the full table on the Sales Report. Every string is
 * a `store.reorder.card.*` key; the derive helper hands over keys and values.
 */
export const ReorderCard: React.FC<ReorderCardProps> = ({ model, loading, planLocked, error }) => {
  const navigate = useNavigate();
  const { t } = useAppTranslation();
  const text = (part: ReorderCardText): string => {
    // A rate inside another line ("not counted · sells 3.5 a week") is itself a key.
    const params = part.params?.rateKey ? { ...part.params, rate: t(`store.reorder.card.${part.params.rateKey}`, { n: part.params.n }) } : part.params;
    return t(`store.reorder.card.${part.key}`, params);
  };
  const pillTone = model.headlineTone;
  const pillBg = pillTone === 'red' ? commandDashboard.color.dangerTint : pillTone === 'amber' ? commandDashboard.color.warningTint : commandDashboard.color.successTint;
  const pillFg = pillTone === 'red' ? commandDashboard.color.dangerText : pillTone === 'amber' ? commandDashboard.color.warningText : commandDashboard.color.successText;

  return (
    <Box
      sx={{
        bgcolor: commandDashboard.color.innerCard,
        border: `1px solid ${commandDashboard.color.innerCardBorder}`,
        borderRadius: `${commandDashboard.radius.innerCard}px`,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', p: '14px 16px', borderBottom: `1px solid ${commandDashboard.color.dividerStrong}` }}>
        <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{t('store.reorder.card.title')}</Typography>
        {!loading && !planLocked && !error && (
          <Box component="span" sx={{ px: '8px', py: '1px', borderRadius: `${commandDashboard.radius.countPill}px`, fontSize: '11.5px', fontWeight: 700, bgcolor: pillBg, color: pillFg }}>
            {model.headlineCount}
          </Box>
        )}
      </Box>

      {loading ? (
        <Box sx={{ p: '14px 16px' }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={44} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      ) : planLocked ? (
        <Box sx={{ p: '16px' }}>
          <Typography sx={{ fontSize: '13px', color: commandDashboard.color.inkSubtle, mb: 1 }}>{t('store.reorder.card.locked')}</Typography>
          <MuiLink component="button" onClick={() => navigate('/app/billing')} underline="hover" sx={{ fontSize: '12.5px', fontWeight: 600, color: commandDashboard.color.accent }}>
            {t('store.reorder.lockedCta')}
          </MuiLink>
        </Box>
      ) : error ? (
        <Box sx={{ p: '16px' }}>
          <Typography sx={{ fontSize: '13px', color: commandDashboard.color.dangerText }}>{error}</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ p: '10px 16px', borderBottom: model.rows.length ? `1px solid ${commandDashboard.color.dividerLight}` : 'none' }}>
            <Typography sx={{ fontSize: '12.5px', color: commandDashboard.color.inkSubtle, lineHeight: 1.4 }}>{model.summary.map(text).join(' · ')}</Typography>
          </Box>
          {model.rows.map((row) => (
            <Box key={row.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', p: '10px 16px', borderBottom: `1px solid ${commandDashboard.color.dividerLight}` }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', mt: '6px', flex: '0 0 auto', bgcolor: dotColor(row.tone) }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 600, lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title}</Typography>
                <Typography sx={{ fontSize: '12px', color: commandDashboard.color.inkSubtle, lineHeight: 1.4 }}>{text(row.meta)}</Typography>
              </Box>
            </Box>
          ))}
          <Box sx={{ p: '10px 16px' }}>
            <MuiLink
              component="button"
              onClick={() => navigate(REORDER_TABLE_HREF)}
              underline="hover"
              sx={{ fontSize: '12.5px', fontWeight: 600, color: commandDashboard.color.accent, '&:hover': { color: commandDashboard.color.accentHover } }}
            >
              {model.moreCount > 0 ? t('store.reorder.card.openTableMore', { count: model.moreCount }) : t('store.reorder.card.openTable')}
            </MuiLink>
          </Box>
        </>
      )}
    </Box>
  );
};
